/** @file CommandForm start values by field path: prefill (ids shown as names), schema default, neutral value. */
import type { Doc } from '@pose2d/format/types';
import { entityName } from './entityName';
import { neutralValue } from './neutralValue';
import type { FieldSpec } from './schemaFields';

/** Value at `path` inside a plain object, or undefined. */
function at(source: unknown, path: readonly string[]): unknown {
  let node = source;
  for (const key of path) node = typeof node === 'object' && node !== null ? (node as Record<string, unknown>)[key] : undefined;
  return node;
}

/** A leaf's start value: null stays null on a nullable field, reference ids become names, JSON becomes text. */
function leafStart(field: FieldSpec, given: unknown, doc: Doc): unknown {
  const value = given === undefined ? field.defaultValue : given;
  if (value === null && field.nullable) return null;
  if (value === undefined) return field.nullable && !field.required ? null : neutralValue(field);
  if (field.kind === 'ref') return typeof value === 'string' ? entityName(doc, field.entity, value) : value;
  return field.kind === 'json' ? JSON.stringify(value) : value;
}

/** Path key (`value.x`) → start value; a group's own key holds `true` (present) or null (a nullable group left out). */
export function initialValues(fields: readonly FieldSpec[], prefill: unknown, doc: Doc): Map<string, unknown> {
  const out = new Map<string, unknown>();
  const visit = (list: readonly FieldSpec[]): void => {
    for (const field of list) {
      const given = at(prefill, field.path);
      if (field.kind !== 'group') {
        out.set(field.path.join('.'), leafStart(field, given, doc));
        continue;
      }
      out.set(field.path.join('.'), given === null || (given === undefined && field.nullable) ? null : true);
      visit(field.fields);
    }
  };
  visit(fields);
  return out;
}
